import React, { useRef } from "react";
import { CheckCircle2, XCircle, Paintbrush, ShieldCheck, Zap, ClipboardList } from "lucide-react";
import { motion, useInView } from "framer-motion";

/* ─── Fade-in wrapper ─────────────────────────────── */
const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── Comparison Table Data ───────────────────────── */
const comparisonRows = [
  {
    feature: "Annual Fee",
    ours: "₹24,999",
    oursNote: null,
    theirs: "₹1,20,000 – ₹1,80,000",
    oursCheck: false,
    oursHighlight: true,
  },
  {
    feature: "Rent Payment",
    ours: "Direct to Owner (INSTANT)",
    oursNote: null,
    theirs: "Held by Middleman",
    oursCheck: true,
    oursHighlight: false,
  },
  {
    feature: "Vacancy Policy",
    ours: "Replacement within days",
    theirs: "No specific timeline",
    oursCheck: true,
    oursHighlight: false,
  },
  {
    feature: "Painting & Cleaning",
    ours: "Managed by us (at Actuals)",
    theirs: "Hidden markups / Commissions",
    oursCheck: true,
    oursHighlight: false,
  },
  {
    feature: "Inspections",
    ours: "Physical Executive Visits",
    theirs: "Reactive",
    oursCheck: true,
    oursHighlight: false,
  },
];

/* ─── Maintenance Protocols Data ─────────────────── */
const protocols = [
  {
    icon: Paintbrush,
    number: "01",
    title: "Professional Painting & Cleaning",
    tagline: "We ensure your property is always move-in ready.",
    iconBg: "bg-amber-500",
    items: [
      {
        label: "Our Role",
        text: "A HousyHunt executive identifies the need, sources competitive quotes, and supervises the labor to ensure quality work.",
      },
      {
        label: "Your Role",
        text: "Painting and cleaning charges are paid by the Owner at actual cost.",
      },
      {
        label: "The Promise",
        text: "We take zero commission on these services. You pay exactly what the vendor charges.",
      },
    ],
  },
];

/* ─── Main Component ──────────────────────────────── */
const ManagementComparison = () => {
  return (
    <section className="w-full bg-gray-50 py-14 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto space-y-20">

        {/* ══════════════════════════════
            MANAGEMENT COMPARISON
        ══════════════════════════════ */}
        <div>
          {/* Section header */}
          <FadeIn>
            <p className="text-red-600 text-xs font-bold tracking-widest uppercase mb-2">
              Side-by-Side
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Management Comparison
            </h2>
            <p className="text-gray-500 text-sm mb-8">
              Comparison based on a property with{" "}
              <span className="font-semibold text-gray-800">₹1,00,000 monthly rent</span>
            </p>
          </FadeIn>

          {/* Table */}
          <FadeIn delay={0.07}>
            <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-lg shadow-gray-900/5 bg-white">
              {/* Table header */}
              <div className="grid grid-cols-3">
                {/* Feature col header */}
                <div className="bg-[#1a3a6b] px-5 py-4 flex items-center">
                  <span className="text-white text-xs font-extrabold tracking-widest uppercase">
                    Feature
                  </span>
                </div>
                {/* HousyHunt col header */}
                <div className="bg-[#1a5cb5] px-5 py-4 flex items-center justify-center text-center">
                  <span className="text-white text-xs font-extrabold tracking-wide uppercase leading-tight">
                    HousyHunt Expert<br />Management
                  </span>
                </div>
                {/* Others col header */}
                <div className="bg-red-600 px-5 py-4 flex flex-col items-center justify-center text-center">
                  <span className="text-white text-xs font-extrabold tracking-wide uppercase leading-tight">
                    Others
                  </span>
                  <span className="text-red-200 text-[10px] font-semibold mt-0.5">
                    (10% – 15% Model)
                  </span>
                </div>
              </div>

              {/* Rows */}
              {comparisonRows.map((row, i) => (
                <motion.div
                  key={row.feature}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className={`grid grid-cols-3 border-t border-gray-100 ${
                    i % 2 === 0 ? "bg-white" : "bg-gray-50/60"
                  }`}
                >
                  {/* Feature name */}
                  <div className="px-5 py-4 flex items-center">
                    <span className="text-gray-800 font-semibold text-sm">{row.feature}</span>
                  </div>

                  {/* HousyHunt value */}
                  <div className="px-4 py-4 flex items-center justify-center gap-2 border-l border-gray-100">
                    {row.oursCheck && (
                      <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
                    )}
                    <span
                      className={`text-sm text-center leading-snug ${
                        row.oursHighlight
                          ? "font-bold text-gray-900 text-base"
                          : "text-gray-700 font-medium"
                      }`}
                    >
                      {row.ours}
                    </span>
                  </div>

                  {/* Others value */}
                  <div className="px-4 py-4 flex items-center justify-center gap-2 border-l border-gray-100">
                    <span className="text-sm text-center text-gray-500 leading-snug flex-1">
                      {row.theirs}
                    </span>
                    <XCircle size={16} className="text-red-500 shrink-0" />
                  </div>
                </motion.div>
              ))}

              {/* Footer savings callout */}
              <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-white text-sm font-semibold text-center sm:text-left">
                  💰 Save up to{" "}
                  <span className="text-yellow-300 font-extrabold">₹1,55,001</span>{" "}
                  every year with HousyHunt vs traditional agencies.
                </p>
                <motion.button
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  className="bg-white text-red-600 font-bold text-xs px-5 py-2.5 rounded-full shrink-0 shadow hover:shadow-md transition-all duration-200"
                >
                  Subscribe @ ₹24,999 →
                </motion.button>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* ══════════════════════════════
            MAINTENANCE & CARE PROTOCOLS
        ══════════════════════════════ */}
        <div>
          {/* Section header */}
          <FadeIn>
            <p className="text-red-600 text-xs font-bold tracking-widest uppercase mb-2">
              How We Operate
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-10">
              Maintenance &amp; Care Protocols
            </h2>
          </FadeIn>

          {/* Protocol Cards */}
          <div className="space-y-8">
            {protocols.map((proto, i) => {
              const Icon = proto.icon;
              return (
                <FadeIn key={proto.title} delay={i * 0.08}>
                  <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
                    {/* Card header */}
                    <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-100">
                      <div
                        className={`w-12 h-12 rounded-2xl ${proto.iconBg} flex items-center justify-center shadow-md shrink-0`}
                      >
                        <Icon size={22} className="text-white" />
                      </div>
                      <div>
                        <p className="text-[11px] text-gray-400 font-bold tracking-widest uppercase mb-0.5">
                          Protocol {proto.number}
                        </p>
                        <h3 className="text-base font-bold text-gray-900">{proto.title}</h3>
                      </div>
                    </div>

                    {/* Card body */}
                    <div className="px-6 py-6 space-y-5">
                      {/* Tagline */}
                      <p className="text-gray-500 text-sm italic">{proto.tagline}</p>

                      {/* Role items */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {proto.items.map((item, j) => {
                          const colors = [
                            {
                              bg: "bg-blue-50",
                              border: "border-blue-100",
                              label: "text-blue-700",
                              dot: "bg-blue-500",
                            },
                            {
                              bg: "bg-amber-50",
                              border: "border-amber-100",
                              label: "text-amber-700",
                              dot: "bg-amber-500",
                            },
                            {
                              bg: "bg-emerald-50",
                              border: "border-emerald-100",
                              label: "text-emerald-700",
                              dot: "bg-emerald-500",
                            },
                          ];
                          const c = colors[j];
                          return (
                            <div
                              key={item.label}
                              className={`${c.bg} border ${c.border} rounded-xl p-4`}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`w-2 h-2 rounded-full ${c.dot} shrink-0`} />
                                <span className={`text-xs font-extrabold ${c.label} uppercase tracking-wide`}>
                                  {item.label}
                                </span>
                              </div>
                              <p className="text-gray-600 text-xs leading-relaxed">{item.text}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ManagementComparison;